import React, { useRef, useState } from 'react';
import TaskOutlined from '@mui/icons-material/TaskOutlined';
import { Box, Button, Stack, styled, Typography } from '@mui/material';
import { InfoCard } from '@/components/shared/info-card';
import { useSkills } from '@/packages/skill-management/context/skills';
import { lineClamp } from '@/utils/line-clamp';

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.info.main,
  backgroundColor: 'white',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontWeight: 500,
}));

const StyledBox = styled(Box)({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: '120px',
  height: '1.5em',
  background:
    'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1))',
});

interface ClampTextProps {
  expanded: boolean;
  onToggle: () => void;
  text: string;
}

const ClampText = ({ expanded, onToggle, text }: ClampTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);

  React.useEffect(() => {
    if (!textRef.current) return;

    const el = textRef.current;

    const fullHeight = el?.scrollHeight ?? 0;

    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    const maxHeight = lineHeight * 3.2;

    setShouldShowToggle(fullHeight > maxHeight);
  }, [text]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography ref={textRef} sx={!expanded ? lineClamp(3) : {}}>
        {text}
      </Typography>

      {!expanded && shouldShowToggle && (
        <>
          <StyledBox />
          <StyledTypography
            position="absolute"
            bottom={0}
            right={0}
            pl={1}
            onClick={onToggle}
          >
            ver mais
          </StyledTypography>
        </>
      )}

      {expanded && shouldShowToggle && (
        <StyledTypography mt={1} onClick={onToggle}>
          ver menos
        </StyledTypography>
      )}
    </Box>
  );
};

export const Feedback = ({
  onEditFeedback,
}: {
  onEditFeedback: () => void;
}) => {
  const { userFeedback } = useSkills();
  const [expanded, setExpanded] = useState(false);

  const summary = userFeedback?.generated_summary ?? '';

  return (
    <Stack gap={2} alignItems="start">
      <InfoCard icon={TaskOutlined} withBorder onClick={onEditFeedback}>
        <Typography variant="h3">Feedback enviado</Typography>
        <Typography sx={lineClamp(1)} color="text.secondary">
          {userFeedback?.content}
        </Typography>
      </InfoCard>

      <Button onClick={onEditFeedback}>Alterar feedback</Button>

      <Stack gap={1.5}>
        <Typography variant="h3" mt={2}>
          Resumo do seu feedback
        </Typography>

        <ClampText
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          text={summary}
        />
      </Stack>
    </Stack>
  );
};
